/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type * as Type from '@/common/interface';

export interface AIMerchantCategoryCardDefinition {
  serviceType: Type.AIMerchantServiceType;
  title: string;
  description: string;
}

export interface AIMerchantCategoryCardItem
  extends AIMerchantCategoryCardDefinition {
  count: number;
}

const aiMerchantCategoryCardHTMLPattern =
  /\s*<div class="ai-merchant-category-card">\s*[\s\S]*?<a class="ai-merchant-category-card-link"[\s\S]*?<\/a>\s*<\/div>\s*/g;

export const aiMerchantCategoryCardDefinitions: AIMerchantCategoryCardDefinition[] =
  [
    {
      serviceType: 'plus_recharge',
      title: '账号充值类服务商',
      description:
        '围绕账号、会员、订阅、充值和到账稳定性整理服务商。有服务好的商家，也有服务差的，希望大家共同测评，创建更好的 Codex 使用环境。',
    },
    {
      serviceType: 'long_code',
      title: '验证码类服务商',
      description:
        '围绕短信、邮箱、接码、验证码成功率和异常处理整理服务商。有服务好的商家，也有服务差的，希望大家共同测评，创建更好的 Codex 使用环境。',
    },
  ];

export const escapeAIMerchantCategoryCardHTML = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const isAIMerchantCategoryCardHTML = (value: string) =>
  value.includes('class="ai-merchant-category-card"');

export const placeAIMerchantCategoryCardAtTop = (
  content: string,
  cardHTML: string,
) => {
  const card = cardHTML.trim();
  const body = (content || '')
    .replace(aiMerchantCategoryCardHTMLPattern, '\n')
    .trim();

  return `${card}${body ? `\n\n${body}` : ''}\n\n`;
};

export const buildAIMerchantCategoryCardHTML = (
  item: AIMerchantCategoryCardItem,
) => {
  const title = escapeAIMerchantCategoryCardHTML(item.title);
  const description = escapeAIMerchantCategoryCardHTML(item.description);
  const count = Math.max(0, Math.floor(Number(item.count) || 0));
  const href = `/ai-merchants?service_type=${encodeURIComponent(
    item.serviceType,
  )}`;

  return `<div class="ai-merchant-category-card">
  <div class="ai-merchant-category-card-kicker">AI服务商分类</div>
  <div class="ai-merchant-category-card-title">${title}</div>
  <p class="ai-merchant-category-card-desc">${description}</p>
  <div class="ai-merchant-category-card-meta">入驻 ${count} 家 · 测评与避坑</div>
  <a class="ai-merchant-category-card-link" href="${href}" data-merchant-service-type="${item.serviceType}">查看分类</a>
</div>`;
};
